import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Dialog } from "@headlessui/react";
import { ArrowLeft, ArrowUp, Home, Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher.jsx";

const getUserData = (t) => {
  const userDataString = localStorage.getItem("user");
  if (userDataString) {
    try {
      return JSON.parse(userDataString);
    } catch (error) {
      console.error("Failed to parse user data:", error);
    }
  }

  return {
    username: localStorage.getItem("username") || t("guest"),
    phone: localStorage.getItem("phone") || "",
    email: localStorage.getItem("email") || "",
    authToken: localStorage.getItem("authToken") || "",
    merchantId: localStorage.getItem("merchantid") || "",
    shopname: localStorage.getItem("shopname") || "",
  };
};

const Header = () => {
  const { authToken, logout } = useAuth();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();

  const imageUrl = `https://static.vecteezy.com/system/resources/previews/026/619/142/original/default-avatar-profile-icon-of-social-media-user-photo-image-vector.jpg`;

  const userData = getUserData(t);

  const [userProfile, setUserProfile] = useState({
    name: userData.username || localStorage.getItem("username") || t("guest"),
    image: imageUrl,
    email: userData.email || localStorage.getItem("email") || "",
    phone: userData.phone || localStorage.getItem("phone") || "",
    shopname: userData.shopname || localStorage.getItem("shopname") || "",
  });

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleLogout = () => {
    logout();
    localStorage.clear();
    window.location.reload();
    setIsModalOpen(false);
    navigate("/");
  };

  useEffect(() => {
    if (authToken) {
      const currentUserData = getUserData(t);
      setUserProfile({
        name:
          currentUserData.username ||
          localStorage.getItem("username") ||
          t("guest"),
        image: imageUrl,
        email: currentUserData.email || localStorage.getItem("email") || "",
        phone: currentUserData.phone || localStorage.getItem("phone") || "",
        shopname:
          currentUserData.shopname || localStorage.getItem("shopname") || "",
      });
    }
  }, [authToken, imageUrl, t]);

  return (
    <header className="w-full top-0 sticky left-0 z-50 bg-white shadow-lg transition-all duration-300">
      <div className="max-w-full mx-auto px-4 lg:px-10 flex justify-between items-center lg:py-4">
        {/* Logo - responsive sizing */}
        <a
          href="/"
          className="hover:font-bold hover:scale-110 flex items-center gap-2 text-xl lg:text-2xl font-bold text-black hover:opacity-90 transition relative"
          id="logo-heading"
        >
          <img
            src="/images/zayverseLogo.png"
            alt={t("logo")}
            className="w-16 h-16 lg:w-16 lg:h-16 lg:p-0 m-0 p-0 object-cover"
          />
          <span className="font-semibold font-playfair lg:text-3xl text-xl hidden sm:inline">
            ZayVerse
          </span>
        </a>

        {/* Desktop Navigation - hidden on mobile */}
        <nav className="hidden md:flex items-center space-x-4 lg:space-x-6 font-semibold">
          <a
            href="/"
            className="hover:font-bold hover:scale-110 hover:opacity-80 transition px-2 py-1 rounded"
          >
            {t("home")}
          </a>
          <a
            href="#category"
            className="hover:font-bold hover:scale-110 hover:opacity-80 transition px-2 py-1 rounded"
          >
            {t("category")}
          </a>
          {/* <a
            href="/customer"
            className="hover:font-bold hover:scale-110 hover:opacity-80 transition px-2 py-1 rounded"
          >
            Customer
          </a> */}
          <a
            href="#features"
            className="hover:font-bold hover:scale-110 hover:opacity-80 transition px-2 py-1 rounded"
          >
            {t("features")}
          </a>
          <a
            href="/price"
            className="hover:font-bold hover:scale-110 hover:opacity-80 transition px-2 py-1 rounded"
          >
            {t("price")}
          </a>
          <a
            href="#contact"
            className="hover:font-bold hover:scale-110 hover:opacity-80 transition px-2 py-1 rounded"
          >
            {t("contactUs")}
          </a>
          <a
            href="/training"
            className="hover:font-bold hover:scale-110 hover:opacity-80 transition px-2 py-1 rounded"
          >
            {t("buildYourSkills")}
          </a>
        </nav>

        {/* Right side: Language switcher + Auth buttons + Mobile menu button */}
        <div className="flex items-center gap-2 lg:gap-4">
          <LanguageSwitcher />

          {/* Authentication buttons - Show based on auth state */}
          {!authToken ? (
            <>
              {/* Desktop Register/Login buttons */}
              <div className="hidden sm:flex gap-2">
                <a
                  href="/register"
                  className="px-3 py-1.5 text-sm font-medium hover:font-bold hover:scale-105 transition rounded-lg"
                >
                  {t("register")}
                </a>
                <a
                  href="/login"
                  className="px-3 py-1.5 text-sm font-medium hover:font-bold hover:scale-105 transition rounded-lg bg-primary-color text-white"
                >
                  {t("login")}
                </a>
              </div>

              {/* Mobile Register/Login button - single button */}
              <div className="sm:hidden flex">
                <a
                  href="/login"
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-color text-white whitespace-nowrap"
                >
                  {t("login")}
                </a>
              </div>
            </>
          ) : (
            // Show profile button when logged in
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-2 p-1 rounded-lg hover:bg-gray-100 transition"
            >
              <div className="relative w-8 h-8 lg:w-9 lg:h-9">
                <div className="absolute inset-0 rounded-full overflow-hidden border-2 border-gray-200">
                  <img
                    src={userProfile.image}
                    className="w-full h-full object-cover"
                    alt={t("profile")}
                    onError={(e) => {
                      e.currentTarget.src = "https://avatar.vercel.sh/default";
                    }}
                  />
                </div>
              </div>
              <span className="hidden sm:inline text-sm font-medium">
                {userProfile.name}
              </span>
            </button>
          )}

          {/* Mobile Menu Button - visible on mobile only */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition"
            onClick={() => setIsSidebarOpen(true)}
            aria-label="Open menu"
            aria-expanded={isSidebarOpen}
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>

        {/* Mobile Sidebar Overlay */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/10 backdrop-blur-sm z-50 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Mobile Sidebar Panel */}
        <div
          className={`fixed top-0 right-0 h-full w-full max-w-sm bg-white z-[60] lg:hidden shadow-3xl transition-transform duration-300 ease-in-out ${
            isSidebarOpen ? "translate-x-0" : "translate-x-full"
          }`}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          <div className="flex flex-col h-full">
            {/* Sidebar Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <span className="font-semibold text-lg">Menu</span>
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition"
                aria-label="Close menu"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
            </div>

            {/* Sidebar Navigation */}
            <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
              <a
                href="/"
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-100 transition"
                onClick={() => setIsSidebarOpen(false)}
              >
                <Home className="w-5 h-5" />
                <span>{t("home")}</span>
              </a>
              <a
                href="#category"
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-100 transition"
                onClick={() => setIsSidebarOpen(false)}
              >
                <Home className="w-5 h-5" />
                <span>{t("category")}</span>
              </a>
              <a
                href="/customer"
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-100 transition"
                onClick={() => setIsSidebarOpen(false)}
              >
                <Home className="w-5 h-5" />
                <span>Customer</span>
              </a>
              <a
                href="#features"
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-100 transition"
                onClick={() => setIsSidebarOpen(false)}
              >
                <Home className="w-5 h-5" />
                <span>{t("features")}</span>
              </a>
              <a
                href="/price"
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-100 transition"
                onClick={() => setIsSidebarOpen(false)}
              >
                <Home className="w-5 h-5" />
                <span>{t("price")}</span>
              </a>
              <a
                href="#contact"
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-100 transition"
                onClick={() => setIsSidebarOpen(false)}
              >
                <Home className="w-5 h-5" />
                <span>{t("contactUs")}</span>
              </a>
              <a
                href="/training"
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-100 transition"
                onClick={() => setIsSidebarOpen(false)}
              >
                <Home className="w-5 h-5" />
                <span>{t("buildYourSkills")}</span>
              </a>
            </nav>

            {/* Sidebar Footer - Auth buttons when logged out */}
            {!authToken ? (
              <div className="p-4 border-t space-y-2">
                <a
                  href="/register"
                  className="block w-full text-center px-4 py-3 rounded-lg border font-medium hover:bg-gray-50 transition"
                  onClick={() => setIsSidebarOpen(false)}
                >
                  {t("register")}
                </a>
                <a
                  href="/login"
                  className="block w-full text-center px-4 py-3 rounded-lg bg-primary-color text-white font-medium hover:opacity-90 transition"
                  onClick={() => setIsSidebarOpen(false)}
                >
                  {t("login")}
                </a>
              </div>
            ) : (
              // Show user info and logout in sidebar when logged in
              <div className="p-4 border-t">
                <div className="flex items-center gap-3 mb-3">
                  <img
                    src={userProfile.image}
                    alt={userProfile.name}
                    className="w-10 h-10 rounded-full"
                  />
                  <div>
                    <p className="font-semibold text-sm">{userProfile.name}</p>
                    <p className="text-xs text-gray-500">{userProfile.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsSidebarOpen(false);
                  }}
                  className="w-full text-center px-4 py-3 rounded-lg border border-red-300 text-red-600 font-medium hover:bg-red-50 transition"
                >
                  {t("logout")}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Scroll to Top Button - hidden on mobile */}
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 lg:bottom-8 lg:right-8 z-50 p-3 bg-primary-color text-white rounded-full shadow-lg transition-all duration-300 hover:opacity-90 hidden sm:flex"
          aria-label={t("scrollToTop")}
        >
          <ArrowUp className="w-5 h-5" />
        </button>

        {/* Profile Modal */}
        <Dialog
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          className="relative z-50"
        >
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm"
            aria-hidden="true"
          />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl border border-gray-200 relative animate-fade-in">
              <button
                className="absolute top-3 right-3 text-black hover:text-gray-700 transition"
                onClick={() => setIsModalOpen(false)}
                aria-label="Close profile"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="text-center">
                <img
                  src={userProfile.image}
                  alt={userProfile.name}
                  className="w-24 h-24 rounded-full mx-auto mb-4 border border-gray-300"
                />
                <h2 className="text-lg font-bold text-black">
                  {userProfile.name}
                </h2>
                <h5 className="text-gray-700 font-semibold mt-1">
                  {userProfile.shopname}
                </h5>
                <div className="mt-4 space-y-2 text-base text-gray-600">
                  <div className="flex items-center justify-center gap-2">
                    <span>☎️</span>
                    <p className="font-medium">{userProfile.phone}</p>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <span>📧</span>
                    <p className="font-medium">{userProfile.email}</p>
                  </div>
                </div>
                <div className="flex justify-center gap-3 mt-6">
                  <button
                    onClick={handleLogout}
                    className="border border-gray-300 text-gray-700 px-4 py-2 rounded-xl hover:bg-gray-100 transition"
                  >
                    {t("logout")}
                  </button>
                </div>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </div>
    </header>
  );
};

export default Header;
